import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { Socket } from 'socket.io-client';
import type {
  MessageSummary,
  NewMessageEvent,
  PreferenceGateResult,
  SendMessagePayload,
  TypingEvent,
  MessagesReadEvent,
} from '@tabbio/types';
import { SOCKET_EVENTS } from '@tabbio/types';
import {
  fetchMessages,
  markMessagesRead,
  sendMessageREST,
  type GateAwareError,
} from '../lib/messaging-api';
import { fetchNotificationUnreadCount } from '../lib/notifications-api';
import { setNotificationUnreadCount } from '../lib/notification-count';
import { useSocket } from './useSocket';
import { useAuth } from './useAuth';
import { QUERY_KEYS } from '../lib/query-keys';

const SEND_ACK_TIMEOUT_MS = 10000;

type SendMessageAck =
  | { status: 'ok'; messageId?: string; message?: MessageSummary }
  | {
      status: 'error';
      message?: string;
      code?: 'RESTRICTED_BY_PREFS' | 'BLOCKED';
      gate?: Extract<PreferenceGateResult, { allowed: false }>;
    };

export type LocalMessageSummary = MessageSummary & {
  localStatus?: 'sending' | 'failed';
  localError?: string;
  retryContent?: string;
};

function createLocalMessage(params: {
  conversationId: string;
  senderId: string | null;
  content: string;
}): LocalMessageSummary {
  const createdAt = new Date().toISOString();
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    conversationId: params.conversationId,
    senderId: params.senderId,
    content: params.content,
    type: 'text',
    attachments: [],
    readBy: [],
    isDeleted: false,
    createdAt,
    updatedAt: createdAt,
    localStatus: 'sending',
    retryContent: params.content,
  };
}

function isMatchingLocalMessage(
  localMessage: LocalMessageSummary,
  serverMessage: MessageSummary,
) {
  return (
    Boolean(localMessage.localStatus) &&
    localMessage.conversationId === serverMessage.conversationId &&
    localMessage.senderId === serverMessage.senderId &&
    localMessage.content === serverMessage.content &&
    localMessage.type === serverMessage.type
  );
}

function upsertServerMessage(
  messages: LocalMessageSummary[],
  serverMessage: MessageSummary,
) {
  let replaced = false;
  const next = messages.map((message) => {
    if (
      message.id === serverMessage.id ||
      (!replaced && isMatchingLocalMessage(message, serverMessage))
    ) {
      replaced = true;
      return serverMessage;
    }
    return message;
  });
  return replaced ? next : [...next, serverMessage];
}

function emitSendMessage(
  socket: Socket,
  payload: SendMessagePayload,
): Promise<SendMessageAck> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Message send timed out'));
    }, SEND_ACK_TIMEOUT_MS);

    socket.emit(SOCKET_EVENTS.SEND_MESSAGE, payload, (ack: SendMessageAck) => {
      clearTimeout(timer);
      resolve(ack);
    });
  });
}

/**
 * Hook for managing messages in a conversation with real-time Socket.IO updates.
 */
export function useMessages(conversationId: string) {
  const [messages, setMessages] = useState<LocalMessageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const { socket } = useSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { t } = useTranslation('common');
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Initial Load ──────────────────────────────────────────────

  useEffect(() => {
    if (!conversationId) return;

    let cancelled = false;

    async function load() {
      try {
        setIsLoading(true);
        const result = await fetchMessages(conversationId, { limit: 30 });
        if (!cancelled) {
          setMessages(result.messages);
          setHasMore(result.hasMore);
        }
      } catch {
        // Silently fail: UI shows empty state
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // ─── Socket Room + Event Listeners ────────────────────────────

  useEffect(() => {
    if (!socket || !conversationId) return;

    // Join the conversation room
    socket.emit(SOCKET_EVENTS.JOIN_CONVERSATION, conversationId);

    const handleNewMessage = (event: NewMessageEvent) => {
      if (event.conversationId !== conversationId) return;
      setMessages((prev) => {
        // Replace matching local pending messages or refresh duplicates from REST fallback.
        return upsertServerMessage(prev, event.message);
      });
    };

    const handleTyping = (event: TypingEvent) => {
      if (event.conversationId !== conversationId) return;
      setTypingUsers((prev) => {
        if (event.isTyping) {
          return [...new Set([...prev, event.userId])];
        }
        return prev.filter((id) => id !== event.userId);
      });
    };

    const handleMessageRead = (event: MessagesReadEvent) => {
      if (event.conversationId !== conversationId) return;
      setMessages((prev) =>
        prev.map((msg) => {
          if (!event.messageIds.includes(msg.id)) return msg;
          const readBy = Array.isArray(msg.readBy) ? msg.readBy : [];
          if (readBy.some((r) => r.userId === event.userId)) return msg;
          return {
            ...msg,
            readBy: [...readBy, { userId: event.userId, readAt: event.readAt }],
          };
        }),
      );
    };

    socket.on(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
    socket.on(SOCKET_EVENTS.TYPING, handleTyping);
    socket.on(SOCKET_EVENTS.MESSAGE_READ, handleMessageRead);

    return () => {
      socket.emit(SOCKET_EVENTS.LEAVE_CONVERSATION, conversationId);
      socket.off(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
      socket.off(SOCKET_EVENTS.TYPING, handleTyping);
      socket.off(SOCKET_EVENTS.MESSAGE_READ, handleMessageRead);
    };
  }, [socket, conversationId]);

  // ─── Actions ──────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (content: string, retryMessageId?: string) => {
      const trimmedContent = content.trim();
      if (!trimmedContent) return;

      const localMessage = retryMessageId
        ? null
        : createLocalMessage({
            conversationId,
            senderId: user?.id ?? null,
            content: trimmedContent,
          });
      const localMessageId = retryMessageId ?? localMessage?.id;
      if (!localMessageId) return;

      setMessages((prev) => {
        if (retryMessageId) {
          return prev.map((message) =>
            message.id === retryMessageId
              ? {
                  ...message,
                  localStatus: 'sending',
                  localError: undefined,
                  retryContent: trimmedContent,
                  updatedAt: new Date().toISOString(),
                }
              : message,
          );
        }
        return localMessage ? [...prev, localMessage] : prev;
      });

      try {
        if (socket?.connected) {
          const ack = await emitSendMessage(socket, {
            conversationId,
            content: trimmedContent,
            type: 'text',
          });

          if (ack.status === 'error') {
            // Carry the gate code/payload into the shared catch below so the
            // socket path gets the same routing as the REST fallback.
            const ackError: GateAwareError = new Error(
              ack.message ?? 'Failed to send message',
            );
            if (ack.code) ackError.code = ack.code;
            if (ack.gate) ackError.gate = ack.gate;
            throw ackError;
          }

          if (ack.message) {
            setMessages((prev) =>
              prev.map((message) =>
                message.id === localMessageId ? ack.message! : message,
              ),
            );
          } else if (ack.messageId) {
            setMessages((prev) => {
              if (prev.some((message) => message.id === ack.messageId)) {
                return prev.filter((message) => message.id !== localMessageId);
              }
              return prev.map((message) =>
                message.id === localMessageId
                  ? {
                      ...message,
                      id: ack.messageId!,
                      localStatus: undefined,
                      localError: undefined,
                      retryContent: undefined,
                    }
                  : message,
              );
            });
          }
        } else {
          const message = await sendMessageREST(conversationId, trimmedContent);
          setMessages((prev) =>
            prev.map((item) => (item.id === localMessageId ? message : item)),
          );
        }

        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
      } catch (error) {
        const gateError = error as GateAwareError;
        if (gateError.code === 'RESTRICTED_BY_PREFS' && gateError.gate) {
          const recipientId = gateError.gate.recipientId;
          queryClient.setQueryData(
            QUERY_KEYS.MESSAGING_GATED_RECIPIENT(recipientId),
            gateError.gate,
          );
          // Drop the local draft so the thread isn't littered with the
          // blocked message, then route to the gated explainer.
          setMessages((prev) =>
            prev.filter((message) => message.id !== localMessageId),
          );
          router.push(`/messages/gated/${recipientId}` as never);
          return;
        }
        if (gateError.code === 'BLOCKED') {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === localMessageId
                ? {
                    ...message,
                    localStatus: 'failed',
                    localError: t('messages.blockedError'),
                    retryContent: trimmedContent,
                  }
                : message,
            ),
          );
          return;
        }
        const localError =
          error instanceof Error ? error.message : 'Failed to send message';
        setMessages((prev) =>
          prev.map((message) =>
            message.id === localMessageId
              ? {
                  ...message,
                  localStatus: 'failed',
                  localError,
                  retryContent: trimmedContent,
                }
              : message,
          ),
        );
        throw error;
      }
    },
    [queryClient, socket, conversationId, user?.id, router, t],
  );

  const retryMessage = useCallback(
    async (messageId: string) => {
      const message = messages.find((item) => item.id === messageId);
      const content = message?.retryContent ?? message?.content;
      if (!content) return;
      await sendMessage(content, messageId);
    },
    [messages, sendMessage],
  );

  const startTyping = useCallback(() => {
    if (!socket?.connected) return;
    socket.emit(SOCKET_EVENTS.TYPING_START, { conversationId });

    // Auto-stop typing after 3 seconds
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit(SOCKET_EVENTS.TYPING_STOP, { conversationId });
    }, 3000);
  }, [socket, conversationId]);

  const stopTyping = useCallback(() => {
    if (!socket?.connected) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socket.emit(SOCKET_EVENTS.TYPING_STOP, { conversationId });
  }, [socket, conversationId]);

  const markRead = useCallback(
    (messageIds: string[]) => {
      if (!messageIds.length) return;

      // Optimistic decrement for instant badge update; server UNREAD_COUNT
      // event (or REST fallback below) reconciles to the authoritative value.
      const decrementBy = messageIds.length;
      queryClient.setQueriesData<number | undefined>(
        { queryKey: QUERY_KEYS.UNREAD_COUNT },
        (current) => {
          if (typeof current !== 'number') return current;
          return Math.max(0, current - decrementBy);
        },
      );

      if (socket?.connected) {
        socket.emit(SOCKET_EVENTS.MARK_READ, { conversationId, messageIds });
        return;
      }
      markMessagesRead(conversationId, messageIds)
        .then(() => {
          // Optimistic decrement above already updated UNREAD_COUNT cache;
          // skip invalidate to avoid an extra REST round-trip per read.
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
          fetchNotificationUnreadCount()
            .then((count) => setNotificationUnreadCount(queryClient, count))
            .catch(() => {});
        })
        .catch(() => {});
    },
    [queryClient, socket, conversationId],
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || !messages.length) return;
    const oldest = messages[0];
    if (!oldest) return;

    try {
      const result = await fetchMessages(conversationId, {
        before: oldest.createdAt,
      });
      setMessages((prev) => [...result.messages, ...prev]);
      setHasMore(result.hasMore);
    } catch {
      // Silently fail
    }
  }, [conversationId, hasMore, messages]);

  return {
    messages,
    isLoading,
    hasMore,
    typingUsers,
    sendMessage,
    retryMessage,
    startTyping,
    stopTyping,
    markRead,
    loadMore,
  };
}
